/*
 * Copyright The OpenTelemetry Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { buildSamplerFromEnv, loadDefaultConfig } from './config';
import { Sampler } from './Sampler';
import { SpanLimits, TracerConfig, GeneralLimits } from './types';
import {
  DEFAULT_ATTRIBUTE_COUNT_LIMIT,
  DEFAULT_ATTRIBUTE_VALUE_LENGTH_LIMIT,
  getEnvWithoutDefaults,
} from '@opentelemetry/core';

/**
 * Function to merge Default configuration (as specified in './config') with
 * user provided configurations.
 */
export function mergeConfig(userConfig: TracerConfig): TracerConfig & {
  sampler: Sampler;
  spanLimits: SpanLimits;
  generalLimits: GeneralLimits;
} {
  const perInstanceDefaults: Partial<TracerConfig> = {
    sampler: buildSamplerFromEnv(),
  };

  const DEFAULT_CONFIG = loadDefaultConfig();

  const target = Object.assign(
    {},
    DEFAULT_CONFIG,
    perInstanceDefaults,
    userConfig
  );

  target.generalLimits = Object.assign(
    {},
    DEFAULT_CONFIG.generalLimits,
    userConfig.generalLimits || {}
  );

  target.spanLimits = Object.assign(
    {},
    DEFAULT_CONFIG.spanLimits,
    userConfig.spanLimits || {}
  );

  return target;
}

/**
 * When general limits are provided and model specific limits are not,
 * configures the model specific limits by using the values from the general ones.
 * @param userConfig User provided tracer configuration
 */
export function reconfigureLimits(userConfig: TracerConfig): TracerConfig {
  const spanLimits = Object.assign({}, userConfig.spanLimits);

  const parsedEnvConfig = getEnvWithoutDefaults();

  /**
   * Reassign span attribute count limit to use first non null value defined by user or use default value
   */
  spanLimits.attributeCountLimit =
    userConfig.spanLimits?.attributeCountLimit ??
    userConfig.generalLimits?.attributeCountLimit ??
    parsedEnvConfig.OTEL_SPAN_ATTRIBUTE_COUNT_LIMIT ??
    parsedEnvConfig.OTEL_ATTRIBUTE_COUNT_LIMIT ??
    DEFAULT_ATTRIBUTE_COUNT_LIMIT;

  /**
   * Reassign span attribute value length limit to use first non null value defined by user or use default value
   */
  spanLimits.attributeValueLengthLimit =
    userConfig.spanLimits?.attributeValueLengthLimit ??
    userConfig.generalLimits?.attributeValueLengthLimit ??
    parsedEnvConfig.OTEL_SPAN_ATTRIBUTE_VALUE_LENGTH_LIMIT ??
    parsedEnvConfig.OTEL_ATTRIBUTE_VALUE_LENGTH_LIMIT ??
    DEFAULT_ATTRIBUTE_VALUE_LENGTH_LIMIT;

  return Object.assign({}, userConfig, { spanLimits });
}
